import React, { useState, useRef } from 'react';
import { X, Plus, Download, Upload } from 'lucide-react';
import { api } from '../../api';

export const AttendanceModal = ({ workshop, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    actual_attendees: '',
    avg_feedback_score: '',
    your_feedback: '',
    reseller_domains: [{ domain: '', attendee_count: '' }]
  });
  const [submitting, setSubmitting] = useState(false);
  const [csvError, setCsvError] = useState('');
  const fileInputRef = useRef(null);

  const downloadCsvTemplate = () => {
    const rows = [
      ['reseller_domain', 'attendee_count'],
      ['partnerdomain1.com', '10'],
      ['partnerdomain2.com', '5'],
    ];
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'reseller_attendance_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCsvUpload = (e) => {
    setCsvError('');
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const text = ev.target.result;
        const lines = text.trim().split('\n').map(l => l.trim()).filter(Boolean);
        if (lines.length < 2) { setCsvError('CSV must have a header row and at least one data row.'); return; }
        const header = lines[0].split(',').map(h => h.trim().toLowerCase());
        const domainIdx = header.indexOf('reseller_domain');
        const countIdx = header.indexOf('attendee_count');
        if (domainIdx === -1 || countIdx === -1) {
          setCsvError('CSV must have columns: reseller_domain, attendee_count');
          return;
        }
        const parsed = lines.slice(1).map(line => {
          const cols = line.split(',').map(c => c.trim().replace(/^"|"$/g, ''));
          return { domain: cols[domainIdx] || '', attendee_count: cols[countIdx] || '' };
        }).filter(r => r.domain);
        if (parsed.length === 0) { setCsvError('No valid rows found in CSV.'); return; }
        setFormData(prev => ({ ...prev, reseller_domains: parsed }));
      } catch {
        setCsvError('Failed to parse CSV. Please use the provided template.');
      }
      e.target.value = '';
    };
    reader.readAsText(file);
  };

  const addResellerDomain = () => {
    if (formData.reseller_domains.length < 20) {
      setFormData({
        ...formData,
        reseller_domains: [...formData.reseller_domains, { domain: '', attendee_count: '' }]
      });
    }
  };

  const removeResellerDomain = (index) => {
    if (formData.reseller_domains.length > 1) {
      const updated = formData.reseller_domains.filter((_, i) => i !== index);
      setFormData({ ...formData, reseller_domains: updated });
    }
  };

  const updateResellerDomain = (index, field, value) => {
    const updated = [...formData.reseller_domains];
    updated[index][field] = value;
    setFormData({ ...formData, reseller_domains: updated });
  };

  const calculateResellerTotal = () => {
    return formData.reseller_domains.reduce((sum, rd) => {
      const count = parseInt(rd.attendee_count) || 0;
      return sum + count;
    }, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const validResellerDomains = formData.reseller_domains
        .filter(rd => rd.domain.trim() && rd.attendee_count)
        .map(rd => ({ domain: rd.domain.trim(), attendee_count: parseInt(rd.attendee_count) }));

      await api.request(`/workshops/${workshop.id}/attendance`, {
        method: 'PUT',
        body: JSON.stringify({
          workshop_id: workshop.id,
          actual_attendees: parseInt(formData.actual_attendees),
          avg_feedback_score: parseFloat(formData.avg_feedback_score),
          reseller_domains: validResellerDomains,
          your_feedback: formData.your_feedback
        })
      });
      onSuccess();
    } catch (err) {
      alert(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto">
      <div className="bg-white rounded-2xl max-w-2xl w-full m-4 my-8 flex flex-col max-h-[90vh]">
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <h2 className="text-lg font-bold text-levelup-dark">Submit Attendance Data</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg">
            <X size={20} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-4">
          <div className="bg-gray-50 rounded-lg p-3 mb-4">
            <p className="font-medium text-levelup-dark text-sm">{workshop.course_title}</p>
            <p className="text-xs text-gray-500">
              {new Date(workshop.delivery_date).toLocaleDateString()} - {workshop.location}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" id="attendance-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Actual Attendees</label>
                <input
                  type="number"
                  className="input-field text-sm"
                  value={formData.actual_attendees}
                  onChange={(e) => setFormData({...formData, actual_attendees: e.target.value})}
                  min="0"
                  required
                  data-testid="attendance-actual-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Avg Feedback Score (out of 5)</label>
                <input
                  type="number"
                  className="input-field text-sm"
                  value={formData.avg_feedback_score}
                  onChange={(e) => setFormData({...formData, avg_feedback_score: e.target.value})}
                  min="0"
                  max="5"
                  step="0.1"
                  placeholder="e.g., 4.7"
                  required
                  data-testid="attendance-feedback-score-input"
                />
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Reseller Attendance</label>
                  <p className="text-xs text-gray-500">Enter reseller domains and attendee counts.</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={downloadCsvTemplate}
                    className="text-xs text-levelup-teal hover:text-levelup-dark flex items-center gap-1 border border-gray-200 rounded px-2 py-1 bg-white"
                    data-testid="download-csv-template"
                    title="Download CSV template"
                  >
                    <Download size={13} /> Template
                  </button>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="text-xs text-levelup-teal hover:text-levelup-dark flex items-center gap-1 border border-gray-200 rounded px-2 py-1 bg-white"
                    data-testid="upload-csv-btn"
                    title="Upload filled CSV"
                  >
                    <Upload size={13} /> Upload CSV
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    className="hidden"
                    onChange={handleCsvUpload}
                    data-testid="csv-file-input"
                  />
                  {formData.reseller_domains.length < 20 && (
                    <button
                      type="button"
                      onClick={addResellerDomain}
                      className="text-xs text-levelup-teal hover:text-levelup-dark flex items-center gap-1"
                    >
                      <Plus size={14} /> Add
                    </button>
                  )}
                </div>
              </div>
              {csvError && (
                <p className="text-xs text-red-500 mb-2" data-testid="csv-error">{csvError}</p>
              )}

              <div className="space-y-2 max-h-32 overflow-y-auto">
                {formData.reseller_domains.map((rd, index) => (
                  <div key={index} className="grid grid-cols-12 gap-2 items-center">
                    <div className="col-span-7">
                      <input
                        type="text"
                        className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-levelup-accent focus:border-transparent"
                        value={rd.domain}
                        onChange={(e) => updateResellerDomain(index, 'domain', e.target.value)}
                        placeholder="e.g., sunshine.com"
                        data-testid={`reseller-domain-${index}`}
                      />
                    </div>
                    <div className="col-span-4">
                      <input
                        type="number"
                        className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-levelup-accent focus:border-transparent"
                        value={rd.attendee_count}
                        onChange={(e) => updateResellerDomain(index, 'attendee_count', e.target.value)}
                        placeholder="0"
                        min="0"
                        data-testid={`reseller-count-${index}`}
                      />
                    </div>
                    <div className="col-span-1 flex justify-center">
                      {formData.reseller_domains.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeResellerDomain(index)}
                          className="p-0.5 text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-2 pt-2 border-t border-gray-200 flex justify-between items-center text-xs">
                <span className="text-gray-500">{formData.reseller_domains.length}/20 rows</span>
                <span className="font-medium text-gray-700">Total: {calculateResellerTotal()}</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Your Feedback (optional)</label>
              <textarea
                className="input-field text-sm"
                rows={2}
                value={formData.your_feedback}
                onChange={(e) => setFormData({...formData, your_feedback: e.target.value})}
                placeholder="Feedback on content, labs, duration..."
                data-testid="attendance-your-feedback-input"
              />
            </div>
          </form>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex-shrink-0">
          <button
            type="submit"
            form="attendance-form"
            disabled={submitting}
            className="w-full bg-levelup-accent text-levelup-darker py-2.5 rounded-lg font-semibold hover:bg-levelup-accent-hover disabled:opacity-50 text-sm"
            data-testid="attendance-submit-button"
          >
            {submitting ? 'Submitting...' : 'Submit Attendance'}
          </button>
        </div>
      </div>
    </div>
  );
};
